import React, { useState } from 'react';
import { useFinanceStore } from '../api/useFinanceStore';
import { Plus, Send, CheckCircle, FileText, X, Trash2, ChevronDown, ChevronUp, Edit2, Save, Banknote, TrendingDown } from 'lucide-react';
import { formatZAR } from '../../../utils/formatters';
import DocumentActionsBar from './DocumentActionsBar';
import { generateQuotePdf, QuotePdfData } from '../../../services/pdfService';

const QuotationManager: React.FC = () => {
    const { quotes, addQuote, updateQuoteStatus, updateQuote, deleteQuote, products, catalogError, catalogSyncing, loadProducts } = useFinanceStore();
    const [showNewQuote, setShowNewQuote] = useState(false);
    const [clientName, setClientName] = useState('');
    const [clientId, setClientId] = useState('');
    const [items, setItems] = useState<Array<any>>([{ description: '', quantity: 1, rate: 0, total: 0 }]);
    const [taxRate, setTaxRate] = useState(15);
    const [terms, setTerms] = useState('');
    const [banking, setBanking] = useState('');
    const [dueDate, setDueDate] = useState('');
    const [expandedQuote, setExpandedQuote] = useState<string | null>(null);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editData, setEditData] = useState<Partial<typeof quotes[0]>>({});
    const [quotePdfMap, setQuotePdfMap] = useState<Record<string, Blob>>({});
    const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

    const subtotal = items.reduce((s, it) => s + it.total, 0);
    const tax = subtotal * (taxRate / 100);
    const total = subtotal + tax;

    const handleAddItem = () => {
        setItems([...items, { description: '', quantity: 1, rate: 0, total: 0 }]);
    };

    const handleAddProduct = (productId: string) => {
        const product = products.find(p => p.id === productId);
        if (!product) {return;}
        const existing = items.find(it => it.description === product.name);
        if (existing) {
            const newItems = items.map(it =>
                it.description === product.name
                    ? { ...it, quantity: it.quantity + 1, total: (it.quantity + 1) * it.rate }
                    : it
            );
            setItems(newItems);
        } else {
            setItems([...items, {
                description: product.name,
                quantity: 1,
                rate: product.price,
                total: product.price,
                productId: product.id,
                productCode: product.id
            }]);
        }
    };

    const handleItemChange = (idx: number, field: keyof typeof items[0], value: string | number) => {
        const newItems = [...items];
        if (field === 'quantity' || field === 'rate') {
            newItems[idx] = { ...newItems[idx], [field]: Number(value) };
            newItems[idx].total = newItems[idx].quantity * newItems[idx].rate;
        } else {
            newItems[idx] = { ...newItems[idx], [field]: value };
        }
        setItems(newItems);
    };

    const handleRemoveItem = (idx: number) => {
        setItems(items.filter((_, i) => i !== idx));
    };

    const handleCreateQuote = async () => {
        if (!clientName || items.length === 0 || total <= 0) {return;}
        const newQuote = {
            clientId: clientId || `client-${Date.now()}`,
            clientName,
            items: items.map(it => ({ ...it, total: it.quantity * it.rate })),
            subtotal,
            tax,
            totalTaxes: tax,
            total,
            taxRate,
            terms,
            banking,
            dueDate,
        };
        const { financeApi } = await import('../api/financeApi');
        const createdQuote = await financeApi.createQuote(newQuote);
        setShowNewQuote(false);
        setClientName('');
        setClientId('');
        setItems([{ description: '', quantity: 1, rate: 0, total: 0 }]);
        setTaxRate(15);
        setTerms('');
        setBanking('');
        setDueDate('');
        setIsGeneratingPdf(true);
        generateQuotePdf({
            ...createdQuote,
            items: createdQuote.items.map(it => ({ ...it, description: it.description ?? '' }))
        }).then(pdf => {
            setQuotePdfMap(prev => ({ ...prev, [createdQuote.id]: pdf }));
            setIsGeneratingPdf(false);
        }).catch(() => {
            setIsGeneratingPdf(false);
        });
    };

    const handleStartEdit = (quote: typeof quotes[0]) => {
        setEditingId(quote.id);
        setEditData({ ...quote });
    };

    const handleSaveEdit = async (id: string) => {
        await updateQuote(id, editData);
        setEditingId(null);
        setEditData({});
    };

    const handleSendQuote = async (id: string) => {
        await updateQuoteStatus(id, 'sent');
    };

    const handleGenerateQuotePdf = async (id: string) => {
        const quote = quotes.find(q => q.id === id);
        if (!quote) { return new Blob(); }
        setIsGeneratingPdf(true);
        try {
            const pdf = await generateQuotePdf(quote as QuotePdfData);
            setQuotePdfMap(prev => ({ ...prev, [id]: pdf }));
            return pdf;
        } catch (error) {
            console.error('Error generating quote PDF:', error);
            return new Blob();
        } finally {
            setIsGeneratingPdf(false);
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h3 className="text-lg font-bold text-slate-800 uppercase tracking-tight">Quotations</h3>
                <button
                    onClick={() => setShowNewQuote(true)}
                    className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-blue-700 transition-all active:scale-95"
                >
                    <Plus size={14} /> New Quote
                </button>
            </div>

            {/* New Quote Form */}
            {showNewQuote && (
                <div className="p-4 bg-blue-50 rounded-2xl border border-blue-200 space-y-3">
                    <div className="flex items-center justify-between mb-2">
                        <div className="text-xs font-black text-blue-800 uppercase tracking-widest">Create New Quotation</div>
                        <button onClick={() => setShowNewQuote(false)} className="text-slate-400 hover:text-slate-600"><X size={16} /></button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <input type="text" placeholder="Client Name *" value={clientName} onChange={e => setClientName(e.target.value)}
                            className="px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-blue-400" />
                        <input type="text" placeholder="Client ID (optional)" value={clientId} onChange={e => setClientId(e.target.value)}
                            className="px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-blue-400" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                            <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Tax Rate (%)</label>
                            <input type="number" value={taxRate} onChange={e => setTaxRate(Number(e.target.value))}
                                className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-blue-400" />
                        </div>
                        <div>
                            <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Due Date</label>
                            <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)}
                                className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-blue-400" />
                        </div>
                    </div>
                    <div>
                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Terms & Conditions</label>
                        <textarea value={terms} onChange={e => setTerms(e.target.value)} rows={2} placeholder="Payment terms, delivery conditions, etc."
                            className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-blue-400" />
                    </div>
                    <div>
                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Banking Details</label>
                        <textarea value={banking} onChange={e => setBanking(e.target.value)} rows={2} placeholder="Bank, account number, branch code, etc."
                            className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-blue-400" />
                    </div>
                    <div className="space-y-2">
                        {items.map((item, idx) => (
                            <div key={`new-item-${idx}-${item.description}`} className="grid grid-cols-12 gap-2 items-center bg-white p-2 rounded-xl border border-slate-200">
                                <div className="col-span-5"><input type="text" placeholder="Description" value={item.description} onChange={e => handleItemChange(idx, 'description', e.target.value)} className="w-full px-2 py-1 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-blue-400" /></div>
                                <div className="col-span-2"><input type="number" placeholder="Qty" value={item.quantity} onChange={e => handleItemChange(idx, 'quantity', e.target.value)} className="w-full px-2 py-1 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-blue-400" /></div>
                                <div className="col-span-2"><input type="number" placeholder="Rate" value={item.rate} onChange={e => handleItemChange(idx, 'rate', e.target.value)} className="w-full px-2 py-1 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-blue-400" /></div>
                                <div className="col-span-2 text-sm font-bold text-slate-700 text-right">{formatZAR(item.total)}</div>
                                <div className="col-span-1"><button onClick={() => handleRemoveItem(idx)} className="text-slate-400 hover:text-red-500"><X size={14} /></button></div>
                            </div>
                        ))}
                        <div className="flex gap-2 items-center">
                            <button onClick={handleAddItem} className="text-xs text-blue-600 font-bold hover:underline">+ Add Line Item</button>
                            <div className="flex-1 min-w-[180px]">
                                {catalogError && (
                                    <div className="text-xs text-red-600 mb-1">{catalogError}</div>
                                )}
                                <select
                                    onChange={e => { handleAddProduct(e.target.value); e.target.value = ''; }}
                                    className="w-full px-2 py-1 text-xs border border-slate-200 rounded-lg focus:outline-none focus:border-blue-400 bg-white"
                                    disabled={catalogSyncing}
                                    defaultValue=""
                                >
                                    <option value="" disabled>{catalogSyncing ? 'Syncing...' : '+ Add from Catalog...'}</option>
                                    {products.filter(p => p.status !== 'inactive').map(p => (
                                        <option key={p.id} value={p.id}>{p.name} — R {p.price.toFixed(2)}</option>
                                    ))}
                                </select>
                                {catalogError && (
                                    <button onClick={loadProducts} className="mt-1 text-blue-600 underline text-xs">
                                        Retry
                                    </button>
                                )}
                            </div>
                        </div>
                        <div className="flex justify-between items-center pt-2 border-t border-blue-200">
                            <div className="text-xs text-slate-500">
                                <div>Subtotal: {formatZAR(subtotal)}</div>
                                <div>Tax ({taxRate}%): {formatZAR(tax)}</div>
                                <div className="font-bold text-slate-800">Total: {formatZAR(total)}</div>
                            </div>
                            <button onClick={handleCreateQuote} disabled={!clientName || total <= 0} className="px-4 py-2 bg-blue-600 text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-blue-700 transition-all disabled:opacity-50">
                                Create Quote
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="grid gap-4">
                {quotes.length === 0 ? (
                    <div className="p-10 text-center bg-slate-50 rounded-2xl border border-dashed border-slate-300 text-slate-400 font-medium">
                        No quotations found. Start by creating one.
                    </div>
                ) : (
                    quotes.map(quote => (
                        <div key={quote.id} className="bg-white rounded-2xl shadow-sm border border-slate-200 group hover:border-blue-300 transition-all overflow-hidden">
                            {editingId === quote.id ? (
                                <div className="p-4 space-y-3">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        <div>
                                            <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Client Name</label>
                                            <input type="text" value={editData.clientName || ''} onChange={e => setEditData({ ...editData, clientName: e.target.value })}
                                                className="w-full px-2 py-1 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-blue-400" />
                                        </div>
                                        <div>
                                            <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Quote Number</label>
                                            <input type="text" value={editData.quoteNumber || ''} onChange={e => setEditData({ ...editData, quoteNumber: e.target.value })}
                                                className="w-full px-2 py-1 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-blue-400" />
                                        </div>
                                        <div>
                                            <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Tax Rate (%)</label>
                                            <input type="number" value={editData.taxRate || 0} onChange={e => setEditData({ ...editData, taxRate: Number(e.target.value) })}
                                                className="w-full px-2 py-1 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-blue-400" />
                                        </div>
                                        <div>
                                            <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Due Date</label>
                                            <input type="date" value={editData.dueDate || ''} onChange={e => setEditData({ ...editData, dueDate: e.target.value })}
                                                className="w-full px-2 py-1 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-blue-400" />
                                        </div>
                                        <div>
                                            <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Total</label>
                                            <input type="number" value={editData.total || 0} onChange={e => setEditData({ ...editData, total: Number(e.target.value) })}
                                                className="w-full px-2 py-1 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-blue-400" />
                                        </div>
                                        <div>
                                            <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Status</label>
                                            <select value={editData.status || 'draft'} onChange={e => setEditData({ ...editData, status: e.target.value as any })}
                                                className="w-full px-2 py-1 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-blue-400 bg-white">
                                                <option value="draft">Draft</option>
                                                <option value="sent">Sent</option>
                                                <option value="approved">Approved</option>
                                                <option value="invoiced">Invoiced</option>
                                                <option value="cancelled">Cancelled</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div>
                                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Terms & Conditions</label>
                                        <textarea value={editData.terms || ''} onChange={e => setEditData({ ...editData, terms: e.target.value })} rows={2}
                                            className="w-full px-2 py-1 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-blue-400" />
                                    </div>
                                    <div>
                                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Banking Details</label>
                                        <textarea value={editData.banking || ''} onChange={e => setEditData({ ...editData, banking: e.target.value })} rows={2}
                                            className="w-full px-2 py-1 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-blue-400" />
                                    </div>
                                    <div className="flex gap-2">
                                        <button onClick={() => handleSaveEdit(quote.id)} className="flex items-center gap-1 px-3 py-1 bg-green-600 text-white rounded-lg text-xs font-bold"><Save size={12} /> Save</button>
                                        <button onClick={() => { setEditingId(null); setEditData({}); }} className="px-3 py-1 bg-slate-200 text-slate-600 rounded-lg text-xs font-bold">Cancel</button>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div className="p-4 flex items-center justify-between">
                                        <div className="flex items-center gap-4">
                                            <div className="p-3 bg-slate-100 rounded-xl text-slate-600 group-hover:bg-blue-100 group-hover:text-blue-600 transition-colors">
                                                <FileText size={20} />
                                            </div>
                                            <div>
                                                <div className="font-bold text-slate-800">{formatZAR(quote.total)}</div>
                                                <div className="text-xs text-slate-600">
                                                    <span className="font-semibold">{quote.quoteNumber || quote.id}</span>
                                                    {quote.clientName && <span className="text-slate-400"> • {quote.clientName}</span>}
                                                </div>
                                                <div className="text-[10px] text-slate-400 uppercase font-black tracking-widest">
                                                    {quote.status} • {quote.createdAt}
                                                    {quote.dueDate && ` • Due ${quote.dueDate}`}
                                                    {quote.items?.length > 0 && ` • ${quote.items.length} item(s)`}
                                                    {quote.taxRate ? ` • Tax ${quote.taxRate}%` : ''}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex gap-2 items-center">
                                            <button onClick={() => handleStartEdit(quote)} className="p-2 text-slate-400 hover:text-blue-600 transition-colors" title="Edit"><Edit2 size={16} /></button>
                                            {quote.status === 'draft' && (
                                                <button onClick={() => handleSendQuote(quote.id)} className="p-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-all flex items-center gap-1 text-xs font-bold"><Send size={14} /> Send</button>
                                            )}
                                            {quote.status === 'sent' && (
                                                <button onClick={() => updateQuoteStatus(quote.id, 'approved')} className="p-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-all" title="Mark Approved"><CheckCircle size={16} /></button>
                                            )}
                                            {quote.status === 'approved' && (
                                                <button onClick={() => updateQuoteStatus(quote.id, 'invoiced')} className="p-2 bg-purple-50 text-purple-600 rounded-lg hover:bg-purple-100 transition-all" title="Mark Invoiced"><FileText size={16} /></button>
                                            )}
                                            <button onClick={() => setExpandedQuote(expandedQuote === quote.id ? null : quote.id)} className="p-2 text-slate-400 hover:text-slate-600 transition-colors" title="View Details">
                                                {expandedQuote === quote.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                            </button>
                                            <button onClick={() => deleteQuote(quote.id)} className="p-2 text-slate-400 hover:text-red-500 transition-colors" title="Delete"><Trash2 size={16} /></button>
                                        </div>
                                    </div>
                                    {expandedQuote === quote.id && (
                                        <div className="px-4 pb-4 border-t border-slate-100 pt-3 space-y-3">
                                            {quote.terms && (
                                                <div>
                                                    <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Terms & Conditions</div>
                                                    <div className="text-xs text-slate-600 whitespace-pre-wrap">{quote.terms}</div>
                                                </div>
                                            )}
                                            {quote.banking && (
                                                <div>
                                                    <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1 flex items-center gap-1"><Banknote size={10} /> Banking Details</div>
                                                    <div className="text-xs text-slate-600 whitespace-pre-wrap">{quote.banking}</div>
                                                </div>
                                            )}
                                            {quote.items && quote.items.length > 0 && (
                                                <>
                                                    <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Line Items</div>
                                                    <div className="space-y-1">
                                                        {quote.items.map((item, i) => (
                                                            <div key={`line-${i}-${item.description}`} className="flex justify-between text-xs text-slate-600 bg-slate-50 px-3 py-2 rounded-lg">
                                                                <span>{item.description || `Item ${i + 1}`}</span>
                                                                <span className="font-semibold">{formatZAR(item.total || item.rate * item.quantity)}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                    <div className="pt-2 border-t border-slate-100 space-y-1 text-xs">
                                                        <div className="flex justify-between"><span className="text-slate-500">Subtotal:</span><span>{formatZAR(quote.subtotal)}</span></div>
                                                        <div className="flex justify-between"><span className="text-slate-500">Tax ({quote.taxRate || 15}%):</span><span>{formatZAR(quote.tax)}</span></div>
                                                        <div className="flex justify-between font-bold text-slate-800"><span>Total:</span><span>{formatZAR(quote.total)}</span></div>
                                                    </div>
                                                </>
                                            )}
                                            {quote.id && (
                                                <DocumentActionsBar
                                                    kind="quote"
                                                    reference={quote.id}
                                                    pdf={quotePdfMap[quote.id] || new Blob()}
                                                    clientName={quote.clientName}
                                                    documentLabel={`Quote ${quote.quoteNumber || quote.id}`}
                                                    onAfterAction={() => handleGenerateQuotePdf(quote.id)}
                                                />
                                            )}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default QuotationManager;